import { useState, useEffect, useCallback } from 'react';
import apiV2 from '@/middlewares/apiV2';

const EMPTY_CREDITS = { sample_clearances: 0, songs_analyzed: 0 };

/**
 * The credit type a top-up pack grants, i.e. the single key of its `credits`
 * object that is greater than zero.
 * @param {Object} [granted] The pack's `credits` object
 * @returns {{creditType: (string|null), creditsGranted: number}}
 */
const grantOf = (granted) => {
    const entry = Object.entries(granted || {}).find(([, value]) => value > 0);
    return entry
        ? { creditType: entry[0], creditsGranted: entry[1] }
        : { creditType: null, creditsGranted: 0 };
};

/**
 * Normalise the GET /api/v2/me/entitlements payload.
 * `membership` is null for non-members; the API rejects a top-up without an
 * active membership, so packs are only offered to members. `credits` is the
 * whole balance and `credits_expiring` the part of it that dies at the end of
 * the billing period, so the two are never summed. All money in this payload is
 * whole dollars.
 * @param {Object} [data] Raw response body
 * @returns {{membership: (Object|null), isMember: boolean, credits: Object, creditsExpiring: Object, creditsExpireAt: (string|null), products: Array, topUps: Array}}
 */
export const mapEntitlementsResponse = (data) => {
    const membership = (data && data.membership) || null;
    const products = (data && data.products) || [];

    return {
        membership,
        isMember: Boolean(membership),
        credits: { ...EMPTY_CREDITS, ...((data && data.credits) || {}) },
        creditsExpiring: { ...EMPTY_CREDITS, ...((data && data.credits_expiring) || {}) },
        creditsExpireAt: (data && data.credits_expire_at) || null,
        // Every product with the price this user pays, which checkout prices the cart from.
        products,
        topUps: membership
            ? products
                  .filter((product) => product.product_type === 'topup')
                  .map((product) => ({
                      id: product.id,
                      name: product.name,
                      priceId: product.stripe_price_id,
                      price: (product.pricing && product.pricing.yours) || product.amount || 0,
                      ...grantOf(product.credits)
                  }))
            : []
    };
};

/**
 * Custom hook to fetch the current user's membership, credit balances and the
 * top-up packs they can buy. The endpoint is authenticated and a 401 logs the
 * user out, so a page a guest can reach has to pass `enabled` as false for them.
 * @param {boolean} [enabled] Whether to call the API at all
 * @returns {{
 *   entitlements: {membership: (Object|null), isMember: boolean, credits: Object, topUps: Array},
 *   loading: boolean,
 *   error: any,
 *   refetch: Function
 * }}
 */
const useEntitlements = (enabled = true) => {
    const [entitlements, setEntitlements] = useState(() => mapEntitlementsResponse());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchEntitlements = useCallback(async () => {
        if (!enabled) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await apiV2.get('/me/entitlements');

            setEntitlements(mapEntitlementsResponse(response.data));
        } catch (err) {
            console.error('Error fetching entitlements:', err);
            setError(err.response?.data?.message || 'Failed to fetch entitlements');
        } finally {
            setLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        fetchEntitlements();
    }, [fetchEntitlements]);

    return {
        entitlements,
        loading,
        error,
        refetch: fetchEntitlements
    };
};

export default useEntitlements;
